import { useState, useEffect, useRef, useMemo } from "react";
import {
  Search, Gauge, LayoutDashboard, Car, Ban, Mail, FileSignature, Truck, QrCode, Star,
  DollarSign, Users, CalendarDays, ShieldAlert, BarChart3, UserCheck, Receipt, Gift,
  Wrench, Bell, Upload, Camera, Calculator, ClipboardList, Plug, CornerDownLeft, Power, Megaphone,
} from "lucide-react";

// Every admin destination + the words the owner might search for.
const DESTINATIONS = [
  { tab: "command", label: "Command Center", icon: Gauge, keywords: "home pulse overview mission control briefing overdue late fees" },
  { tab: "dashboard", label: "Dashboard / Operations", icon: LayoutDashboard, keywords: "operations ops today" },
  { tab: "bookings", label: "Bookings", icon: Car, keywords: "reservations rentals orders new booking trips" },
  { tab: "cancelled", label: "Cancelled Bookings", icon: Ban, keywords: "canceled cancellations void" },
  { tab: "contacts", label: "Messages", icon: Mail, keywords: "contact form leads inquiries messages" },
  { tab: "agreements", label: "Agreements", icon: FileSignature, keywords: "contracts e-sign signed rental agreement documents pdf" },
  { tab: "fleet", label: "Fleet", icon: Truck, keywords: "cars vehicles inventory pricing rates add car" },
  { tab: "share", label: "Share / QR", icon: QrCode, keywords: "qr code link marketing share" },
  { tab: "reviews", label: "Reviews", icon: Star, keywords: "ratings testimonials feedback approve review" },
  { tab: "money", label: "Money", icon: DollarSign, keywords: "payments charge card invoice revenue collect balance stripe" },
  { tab: "customers", label: "Customers & Do-Not-Rent", icon: Users, keywords: "do not rent dnr block blacklist banned ban clients profiles history" },
  { tab: "availability", label: "Availability", icon: CalendarDays, keywords: "calendar block dates schedule unavailable" },
  { tab: "recovery", label: "Recovery & Repossession", icon: ShieldAlert, keywords: "repossession repo overdue late tow chase recover stolen" },
  { tab: "analytics", label: "Traffic / Analytics", icon: BarChart3, keywords: "traffic visitors stats analytics reports" },
  { tab: "drivers", label: "Drivers", icon: UserCheck, keywords: "driver license verification id selfie" },
  { tab: "citations", label: "Citations", icon: Receipt, keywords: "tickets tolls violations fines" },
  { tab: "rewards", label: "Rewards / Loyalty", icon: Gift, keywords: "loyalty points referral rewards" },
  { tab: "marketing", label: "Marketing Command Center", icon: Megaphone, keywords: "leads utm campaigns upfirst ai calls webhook sources roi tiktok facebook instagram google" },
  { tab: "reservations", label: "Reservation Requests", icon: ClipboardList, keywords: "reservation requests pre-screening approve decline phone screening private booking link queue" },
  { tab: "maintenance", label: "Maintenance", icon: Wrench, keywords: "service repair mechanic oil ledger shop" },
  { tab: "notifications", label: "Notifications Health", icon: Bell, keywords: "alerts sms email notify health status" },
  { tab: "automation", label: "Automation Control Center", icon: Power, keywords: "automations toggles on off pause kill switch enable disable suppress control" },
  { tab: "imports", label: "Import Contacts", icon: Upload, keywords: "import upload csv contacts migrate" },
  { tab: "inspections", label: "Inspections", icon: Camera, keywords: "inspection photos condition damage check-in check-out" },
  { tab: "pnl", label: "Tax / P&L", icon: Calculator, keywords: "profit loss tax pnl accounting expenses income" },
  { tab: "playbook", label: "Checkout Playbook", icon: ClipboardList, keywords: "checkout steps handover process playbook" },
  { tab: "system", label: "System Center", icon: Plug, keywords: "integrations ghl gohighlevel pipeline email test api health logs automation roadmap" },
];

export default function AdminSearch({ onNavigate }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return DESTINATIONS;
    return DESTINATIONS.filter((d) =>
      d.label.toLowerCase().includes(q) || d.keywords.includes(q) || d.tab.includes(q) ||
      q.split(" ").every((w) => (d.label + " " + d.keywords).toLowerCase().includes(w))
    );
  }, [query]);

  useEffect(() => { setActive(0); }, [query]);

  // ⌘K / Ctrl+K to focus, Esc to close
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (tab) => {
    onNavigate(tab);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && results[active]) { e.preventDefault(); go(results[active].tab); }
  };

  return (
    <div ref={wrapRef} className="relative w-full max-w-xl" data-testid="admin-search">
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          ref={inputRef}
          data-testid="admin-search-input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onInputKey}
          placeholder="Search admin… (try “do not rent”, “bookings”, “recovery”)"
          className="w-full bg-[#0C0C0C] border border-white/15 rounded-lg pl-10 pr-16 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/60"
        />
        <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-white/40 border border-white/15 rounded px-1.5 py-0.5 hidden sm:block">⌘K</kbd>
      </div>

      {open && (
        <div data-testid="admin-search-results" className="absolute z-50 mt-2 w-full bg-[#0C0C0C] border border-white/15 rounded-xl shadow-2xl overflow-hidden max-h-[60vh] overflow-y-auto">
          {results.length === 0 && (
            <div className="px-4 py-6 text-center text-white/40 text-sm">No sections match “{query}”.</div>
          )}
          {results.map((d, i) => {
            const Icon = d.icon;
            return (
              <button
                key={d.tab}
                data-testid={`admin-search-result-${d.tab}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(d.tab)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === active ? "bg-[#F2B705]/15 text-white" : "text-white/70 hover:bg-white/5"}`}
              >
                <Icon size={16} className={i === active ? "text-[#F2B705]" : "text-white/40"} />
                <span className="text-sm font-medium flex-1">{d.label}</span>
                {i === active && <CornerDownLeft size={13} className="text-white/40" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
